import React, { FC, useCallback, useEffect, useState } from 'react';
import { ApiProfile } from '../../api/auth';
import Loader from '../../components/Loader';
import { Button, Form, Grid, Modal, Segment, Table } from '../../kit';
import { c } from '../../lib/util';
import { CoachApi } from '../../models/Coach';
import styles from './styles.module.scss';
import { useProfile } from '../../lib/auth';
import { needSubscription } from '../../components/NeedSubscriptionModal';
import Timeline from '../../modules/Timeline';
import { request } from '../../api';
import { showFullscreenMessage } from '../../components/FullscreenMessage';

interface ProfileDetailProps {
    permalink: string,
}

const ProfileDetail: FC<ProfileDetailProps> = ({ permalink }): JSX.Element => {
    const [ profileDone, setProfileDone ] = useState<number>(0);
    const [ profile, setProfile ] = useState<ApiProfile>();
    const [ showDm, setShowDm ] = useState<boolean>(false);
    const [ dmContent, setDmContent ] = useState<string>('');
    const auth = useProfile();

    useEffect(() => {
        if (!auth || auth.id !== profile?.id) return;
        let counter = 0;
        if (auth.function) counter++;
        if (auth.work_area.length > 0) counter++;
        if (auth.avatar) counter++;
        if (auth.expertise) counter++;
        if (auth.organisation) counter++;
        if (auth.city) counter++;
        if (auth.introduction) counter++;
        setProfileDone(Math.ceil((100 / 7) * counter));
        (window as any).AOS.init();
    }, [auth, profile]);

    useEffect(() => {
        if (!profile) return;
        (window as any).AOS.init();
    }, [profile]);

    const fetch = useCallback(() => {
        CoachApi.getByPermalink(permalink).then(({ data }) => {
            setProfile(data.data);
        });
    }, [permalink]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    const handleDm = (): void => {
        if (auth?.subscription === 'limited') {
            needSubscription(auth);
            return;
        }
        
        setShowDm(true);
    }

    const sendDm = (): void => {
        if (!auth || !profile || dmContent === '') return;

        request.post(`conversations/${profile.id}`, { content: dmContent }).then(() => {
            setShowDm(false);
            setDmContent('');
            showFullscreenMessage('Bericht verzonden');
        });
    }

    return profile ? (<>
        <div className="container" style={{ maxWidth: 1140 }}>
            <Grid.Row>
                <Grid.Column lg={4}>
                    <div className={c(['person', styles.profile])}>
                        <div
                            className="person__avatar person__avatar--square"
                            style={{ backgroundImage: `url(${profile.avatar || `${process.env.REACT_APP_WP_URL}assets/images/placeholder.jpg`}` }}
                        />
                        <div className="person__meta">
                            <h2>{profile.name}</h2>
                            <div className="person__tags">
                                {profile.spat_coach && <div>SPAT coach</div>}
                                {profile.spat_partner && <div>SPAT partner</div>}
                                {(profile.academy_offline || profile.academy_online) && <div>
                                    { profile.academy_offline ? 'Offline' : 'Online' } Academy
                                </div>}
                            </div>
                            {profile.is_available && (
                                <div style={{ marginBottom: 16 }}>
                                    <i className="far fa-check" style={{ marginRight: 8 }}></i>
                                    Beschikbaar voor opdrachten
                                </div>
                            )}
                            {auth?.id !== profile.id && profile.subscription !== 'limited' && (
                                <Button
                                    color="secondary"
                                    label="Verstuur privé bericht"
                                    onClick={() => handleDm()}
                                />
                            )}
                        </div>
                    </div>

                    <Segment>
                        <Table fluid style={{ marginTop: -32 }}>
                            <tbody>
                                <Table.Row>
                                    <Table.Cell>
                                        <b>Organisatie</b>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {profile.organisation || '-'}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>
                                        <b>Functie</b>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {profile.function || '-'}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>
                                        <b>Expertise</b>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {profile.expertise || '-'}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>
                                        <b>Woonplaats</b>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {profile.city || '-'}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>
                                        <b>Werkgebied</b>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {profile.work_area.join(', ')}
                                    </Table.Cell>
                                </Table.Row>
                            </tbody>
                        </Table>
                    </Segment>

                    {/* <Segment header="Academy">
                        <Segment style={{ marginBottom: 16, padding: 16 }}>
                            <Checkbox
                                checked={profile.academy_offline}
                                label="Offline SPAT academy afgerond"
                                toggle
                            />
                        </Segment>
                        <Segment style={{ padding: 16, marginBottom: 0 }}>
                            <Checkbox
                                checked={profile.academy_online}
                                label="Online SPAT academy afgerond"
                                toggle
                            />
                        </Segment>
                    </Segment> */}
                </Grid.Column>
                <Grid.Column lg={8}>
                    {profile.id === auth?.id && (
                        <Segment className={styles.profileProgress}>
                            <div>
                                <p style={{ fontSize: 13 }}>Je profiel is voor <span>{profileDone}%</span> gevuld{profileDone < 100 ? ', vul deze nu aan!' : '.'}</p>
                                <div className={styles.progress}>
                                    <div style={{ width: `${profileDone}%` }} />
                                </div>
                            </div>
                            <Button
                                color="primary"
                                label={profileDone < 100 ? 'Vul je profiel aan' : 'Wijzig je profiel'}
                                href="/community/account"
                            />
                            {auth?.id === profile.id && auth?.subscription === 'limited' && (
                                <Button
                                    color="secondary"
                                    label="Upgrade je profiel"
                                    onClick={() => needSubscription(auth)}
                                />
                            )}
                        </Segment>
                    )}
                    {profile.introduction && (
                        <Segment header="Introductie">
                            <p dangerouslySetInnerHTML={{ __html: profile.introduction}} />
                        </Segment>
                    )}

                    <div className={styles.stats}>
                        <Grid.Row>
                            <Grid.Column lg={6}>
                                <Segment className={styles.stat}>
                                    <div>{profile.exercise_count}</div>
                                    <p>
                                        Oefeningen toegevoegd door {profile.first_name}
                                    </p>
                                </Segment>
                            </Grid.Column>
                            <Grid.Column lg={6}>
                                <Segment className={styles.stat}>
                                    <div>{profile.knowledge_count}</div>
                                    <p>
                                        Kennisbank items toegevoegd door {profile.first_name}
                                    </p>
                                </Segment>
                            </Grid.Column>
                        </Grid.Row>
                    </div>

                    {auth && (
                        <Timeline
                            auth={auth}
                            profile={profile}
                        />
                    )}
                </Grid.Column>
            </Grid.Row>
        </div>
        <Modal
            header="Verstuur een privébericht"
            footer={(
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color="transparent"
                        label="Annuleren"
                        onClick={() => { setDmContent(''); setShowDm(false); }}
                    />
                    <Button
                        color="primary"
                        label="versturen"
                        onClick={() => sendDm()}
                    />
                </div>
            )}
            open={showDm}
        >
            <Form.Textarea
                rows={5}
                onChange={({ value }) => setDmContent(value)}
                value={dmContent}
            />
        </Modal>
    </>) : <Loader />;
}

export default ProfileDetail;
